import * as Yup from "yup";
import { testOptions, englishTestRanges } from "types";

type TestType = "TOEFL" | "IELTS" | "PTE" | "CAE" | "CPE";

const isValidScore = function (
  this: any,
  score: number | undefined | null,
  testType: TestType | ""
): boolean {
  return (
    !testOptions ||
    score === undefined ||
    score === null ||
    (score >= englishTestRanges[testType as TestType].min &&
      score <= englishTestRanges[testType as TestType].max)
  );
};

export const genuineStudentSchema = [
  // Stage 0: Application Linkage (Optional)
  Yup.object({
    applicationId: Yup.string(),
  }),

  // Stage 1: Home Country Ties
  Yup.object({
    // Family & Social substage
    hasImmediateFamilyHome: Yup.boolean()
      .nullable()
      .required(
        "Please indicate if you have immediate family in your home country"
      ),
    immediateFamilyMembers: Yup.array().when("hasImmediateFamilyHome", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string())
          .min(1, "Please select at least one family member"),
      otherwise: (schema) => schema.notRequired(),
    }),
    hasDependents: Yup.boolean()
      .nullable()
      .required("Please indicate if you have dependents"),
    numberOfDependents: Yup.number().when("hasDependents", {
      is: true,
      then: (schema) =>
        schema
          .required("Please enter the number of dependents")
          .min(1, "Must be at least 1"),
      otherwise: (schema) => schema.notRequired(),
    }),
    dependentTypes: Yup.array().when("hasDependents", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string())
          .min(1, "Please select at least one dependent type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    hasFamilyInHost: Yup.boolean()
      .nullable()
      .required("Please indicate if you have family in the host country"),
    familyInHostRelationship: Yup.string().when("hasFamilyInHost", {
      is: true,
      then: (schema) => schema.required("Please specify the relationship"),
      otherwise: (schema) => schema.notRequired(),
    }),
    hasCommunityInvolvement: Yup.boolean()
      .nullable()
      .required("Please indicate your community involvement"),
    communityOrganizationTypes: Yup.array().when("hasCommunityInvolvement", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string())
          .min(1, "Please select at least one organization type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    communityIntegration: Yup.string().required(
      "Please rate your community integration"
    ),
    familyCommunityTies: Yup.string()
      .required("Please describe your family and community ties")
      .max(150, "Maximum 150 words allowed"),

    // Professional & Assets substage
    currentlyEmployed: Yup.boolean()
      .nullable()
      .required("Please indicate your employment status"),
    employmentType: Yup.string().when("currentlyEmployed", {
      is: true,
      then: (schema) => schema.required("Please select employment type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    yearsInPosition: Yup.number().when("currentlyEmployed", {
      is: true,
      then: (schema) =>
        schema
          .required("Please enter years in position")
          .min(0, "Years cannot be negative"),
      otherwise: (schema) => schema.notRequired(),
    }),
    industry: Yup.string().when("currentlyEmployed", {
      is: true,
      then: (schema) => schema.required("Please enter your industry"),
      otherwise: (schema) => schema.notRequired(),
    }),
    positionHeld: Yup.boolean()
      .nullable()
      .when("currentlyEmployed", {
        is: true,
        then: (schema) =>
          schema.required("Please indicate if your position will be held"),
        otherwise: (schema) => schema.notRequired(),
      }),
    mainIncomeSource: Yup.string().when("currentlyEmployed", {
      is: false,
      then: (schema) =>
        schema.required("Please select your main income source"),
      otherwise: (schema) => schema.notRequired(),
    }),
    ownsProperty: Yup.boolean()
      .nullable()
      .required("Please indicate if you own property"),
    propertyTypes: Yup.array().when("ownsProperty", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string())
          .min(1, "Please select at least one property type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    propertyValuationAmount: Yup.number().when("ownsProperty", {
      is: true,
      then: (schema) =>
        schema
          .required("Please enter property valuation amount")
          .min(0, "Amount cannot be negative"),
      otherwise: (schema) => schema.notRequired(),
    }),
    propertyValuationCurrency: Yup.string().when(
      ["ownsProperty", "propertyValuationAmount"],
      {
        is: (ownsProperty: boolean, amount: number) =>
          ownsProperty === true && amount > 0,
        then: (schema) => schema.required("Currency is required"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    ownsBusiness: Yup.boolean()
      .nullable()
      .required("Please indicate if you own a business"),
    businessRole: Yup.string().when("ownsBusiness", {
      is: true,
      then: (schema) => schema.required("Please select your business role"),
      otherwise: (schema) => schema.notRequired(),
    }),
    hasInvestments: Yup.boolean()
      .nullable()
      .required("Please indicate if you have investments"),
    employmentEconomicSituation: Yup.string()
      .required("Please describe your employment and economic situation")
      .max(150, "Maximum 150 words allowed"),
  }),

  // Stage 2: Course & Career
  Yup.object({
    // Employment Background substage
    totalWorkExperience: Yup.number()
      .required("Please enter your total work experience")
      .min(0, "Experience cannot be negative"),
    workRelatedToStudy: Yup.boolean()
      .nullable()
      .required("Please indicate if your work is related to your study field"),
    employerSupportsStudy: Yup.boolean()
      .nullable()
      .required("Please indicate if your employer supports your study"),
    employmentHistoryRelevance: Yup.string()
      .required("Please describe your employment history and its relevance")
      .max(150, "Maximum 150 words allowed"),

    // Course Selection substage
    courseAvailableHome: Yup.boolean()
      .nullable()
      .required(
        "Please indicate if the course is available in your home country"
      ),
    whyStudyAbroadReasons: Yup.array().when("courseAvailableHome", {
      is: true,
      then: (schema) =>
        schema.of(Yup.string()).min(1, "Please select at least one reason"),
      otherwise: (schema) => schema.notRequired(),
    }),
    courseRelatedPrevEducation: Yup.boolean()
      .nullable()
      .required(
        "Please indicate if the course relates to your previous education"
      ),
    courseRequiredLicensing: Yup.boolean()
      .nullable()
      .required("Please indicate if the course is required for licensing"),
    researchedAlternatives: Yup.boolean()
      .nullable()
      .required("Please indicate if you researched alternative providers"),
    researchActivities: Yup.array().when("researchedAlternatives", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string())
          .min(1, "Please select at least one research activity"),
      otherwise: (schema) => schema.notRequired(),
    }),
    consideredOtherCountries: Yup.boolean()
      .nullable()
      .required("Please indicate if you considered other countries"),
    countriesConsidered: Yup.string().when("consideredOtherCountries", {
      is: true,
      then: (schema) => schema.required("Please list the countries considered"),
      otherwise: (schema) => schema.notRequired(),
    }),
    whyThisCourse: Yup.string()
      .required("Please explain why you chose this course")
      .max(150, "Maximum 150 words allowed"),
    whyThisProvider: Yup.string()
      .required("Please explain why you chose this provider")
      .max(150, "Maximum 150 words allowed"),
    whyThisCountry: Yup.string()
      .required("Please explain why you chose this country")
      .max(150, "Maximum 150 words allowed"),

    // Career Planning substage
    postGraduationWorkLocation: Yup.string().required(
      "Please select your post-graduation work location"
    ),
    hasSpecificCareerGoal: Yup.boolean()
      .nullable()
      .required("Please indicate if you have a specific career goal"),
    targetRole: Yup.string().when("hasSpecificCareerGoal", {
      is: true,
      then: (schema) => schema.required("Please describe your target role"),
      otherwise: (schema) => schema.notRequired(),
    }),
    qualificationDemand: Yup.string().required(
      "Please select the demand level for your qualification"
    ),
    expectedIncomeChange: Yup.string().required(
      "Please select your expected income change"
    ),
    qualificationUsePlans: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one qualification use"),
    hasJobOffer: Yup.boolean()
      .nullable()
      .required("Please indicate if you have a job offer"),
    returnToFamilyBusiness: Yup.boolean()
      .nullable()
      .required("Please indicate if you will return to a family business"),
    careerBenefitsPlans: Yup.string()
      .required("Please describe your career benefits and plans")
      .max(150, "Maximum 150 words allowed"),
  }),

  // Stage 3: Immigration & Intent
  Yup.object({
    // Travel & Intent substage
    traveledInternationally: Yup.boolean()
      .nullable()
      .required("Please indicate if you have traveled internationally"),
    countriesVisited: Yup.number().when("traveledInternationally", {
      is: true,
      then: (schema) =>
        schema
          .required("Please enter the number of countries visited")
          .min(1, "Must be at least 1"),
      otherwise: (schema) => schema.notRequired(),
    }),
    alwaysCompliedVisa: Yup.boolean()
      .nullable()
      .when("traveledInternationally", {
        is: true,
        then: (schema) =>
          schema.required(
            "Please indicate if you always complied with visa conditions"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    expectedProgramDuration: Yup.number()
      .required("Please enter the expected program duration")
      .min(1, "Duration must be at least 1 month"),
    planFurtherStudy: Yup.boolean()
      .nullable()
      .required("Please indicate if you plan further study"),
    furtherStudyType: Yup.string().when("planFurtherStudy", {
      is: true,
      then: (schema) =>
        schema.required("Please select the type of further study"),
      otherwise: (schema) => schema.notRequired(),
    }),
    seekWorkAuthorization: Yup.boolean()
      .nullable()
      .required("Please indicate if you will seek work authorization"),
    intendedWorkDuration: Yup.string().when("seekWorkAuthorization", {
      is: true,
      then: (schema) =>
        schema.required("Please select the intended work duration"),
      otherwise: (schema) => schema.notRequired(),
    }),
    interestedPermanentResidence: Yup.boolean()
      .nullable()
      .required("Please indicate your interest in permanent residence"),
    permanentResidenceLevel: Yup.string().when("interestedPermanentResidence", {
      is: true,
      then: (schema) => schema.required("Please select your level of interest"),
      otherwise: (schema) => schema.notRequired(),
    }),
    planReturnHome: Yup.string().required(
      "Please select when you plan to return home"
    ),

    // Compliance & History substage
    everHadVisaRefused: Yup.boolean()
      .nullable()
      .required("Please indicate if you have had a visa refused"),
    visaRefusalCount: Yup.string().when("everHadVisaRefused", {
      is: true,
      then: (schema) => schema.required("Please enter the number of refusals"),
      otherwise: (schema) => schema.notRequired(),
    }),
    mostRecentRefusalCountry: Yup.string().when("everHadVisaRefused", {
      is: true,
      then: (schema) =>
        schema.required("Please enter the most recent refusal country"),
      otherwise: (schema) => schema.notRequired(),
    }),
    mostRecentRefusalYear: Yup.date()
      .nullable()
      .when("everHadVisaRefused", {
        is: true,
        then: (schema) =>
          schema.required("Please select the most recent refusal year"),
        otherwise: (schema) => schema.notRequired(),
      }),
    everHadVisaCancelled: Yup.boolean()
      .nullable()
      .required("Please indicate if you have had a visa cancelled"),
    everOverstayedVisa: Yup.boolean()
      .nullable()
      .required("Please indicate if you have overstayed a visa"),
    everDeported: Yup.boolean()
      .nullable()
      .required("Please indicate if you have been deported"),
    everBannedEntry: Yup.boolean()
      .nullable()
      .required("Please indicate if you have been banned from entry"),
    everWorkedWithoutAuth: Yup.boolean()
      .nullable()
      .required("Please indicate if you have worked without authorization"),
    familyMemberVisaIssues: Yup.boolean()
      .nullable()
      .required("Please indicate if any family member has had visa issues"),
    visaHistoryExplanation: Yup.string().when(
      [
        "everHadVisaRefused",
        "everHadVisaCancelled",
        "everOverstayedVisa",
        "everDeported",
        "everBannedEntry",
        "everWorkedWithoutAuth",
        "familyMemberVisaIssues",
      ],
      {
        is: (...values: boolean[]) => values.some((v) => v === true),
        then: (schema) =>
          schema
            .required("Please provide a visa history explanation")
            .max(150, "Maximum 150 words allowed"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),

    // Current Status substage
    studiedInHostBefore: Yup.boolean()
      .nullable()
      .required(
        "Please indicate if you have studied in the host country before"
      ),
    completedPreviousProgram: Yup.boolean()
      .nullable()
      .when("studiedInHostBefore", {
        is: true,
        then: (schema) =>
          schema.required(
            "Please indicate if you completed the previous program"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    previousProgramNonCompletionReason: Yup.string().when(
      ["studiedInHostBefore", "completedPreviousProgram"],
      {
        is: (studied: boolean, completed: boolean) =>
          studied === true && completed === false,
        then: (schema) =>
          schema.required("Please select the reason for non-completion"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    previousStudyHistory: Yup.string().when("studiedInHostBefore", {
      is: true,
      then: (schema) =>
        schema
          .required("Please provide your previous study history")
          .max(150, "Maximum 150 words allowed"),
      otherwise: (schema) => schema.notRequired(),
    }),
    currentlyInHostCountry: Yup.boolean()
      .nullable()
      .required("Please indicate if you are currently in the host country"),
    currentVisaType: Yup.string().when("currentlyInHostCountry", {
      is: true,
      then: (schema) => schema.required("Please select your current visa type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    currentVisaExpiry: Yup.date()
      .nullable()
      .when("currentlyInHostCountry", {
        is: true,
        then: (schema) => schema.required("Please enter your visa expiry date"),
        otherwise: (schema) => schema.notRequired(),
      }),
    changingVisaType: Yup.boolean()
      .nullable()
      .required("Please indicate if you are changing visa type"),
    visaChangeRationale: Yup.string().when("changingVisaType", {
      is: true,
      then: (schema) =>
        schema
          .required("Please explain your reason for changing visa type")
          .max(150, "Maximum 150 words allowed"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),

  // Stage 4: Financial & Preparation
  Yup.object({
    // Funding substage
    primaryFundingSource: Yup.string().required(
      "Please select your primary funding source"
    ),
    fundingFullySecured: Yup.boolean()
      .nullable()
      .required("Please indicate if your funding is fully secured"),
    totalFundsAvailable: Yup.number().required(
      "Please enter the total funds available"
    ),
    totalFundsCurrency: Yup.string().when("totalFundsAvailable", {
      is: (val: number) => val && val > 0,
      then: (schema) => schema.required("Currency is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    scholarshipLoanApproved: Yup.boolean()
      .nullable()
      .when("primaryFundingSource", {
        is: (val: string) =>
          ["scholarship", "government_loan", "bank_loan"].includes(val),
        then: (schema) =>
          schema.required(
            "Please indicate if the scholarship/loan is approved"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    hasFinancialSponsors: Yup.boolean()
      .nullable()
      .required("Please indicate if you have a financial sponsor"),

    // Sponsor 1
    sponsor1FamilyName: Yup.string().test(
      "conditional-required",
      "Please enter family name",
      function (value) {
        const {
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor1GivenName: Yup.string().test(
      "conditional-required",
      "Please enter given name",
      function (value) {
        const {
          sponsor1FamilyName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor1FamilyName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor1Country: Yup.string().test(
      "conditional-required",
      "Please select country",
      function (value) {
        const {
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor1Relationship: Yup.string().test(
      "conditional-required",
      "Please select relationship",
      function (value) {
        const {
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Occupation,
          sponsor1Income,
          sponsor1Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor1Occupation: Yup.string().test(
      "conditional-required",
      "Please enter occupation",
      function (value) {
        const {
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Income,
          sponsor1Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Income,
          sponsor1Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor1Income: Yup.number()
      .min(0, "Income cannot be negative")
      .test(
        "conditional-required",
        "Please enter annual income",
        function (value) {
          const {
            sponsor1FamilyName,
            sponsor1GivenName,
            sponsor1Country,
            sponsor1Relationship,
            sponsor1Occupation,
            sponsor1Currency,
          } = this.parent;
          const hasAnyValue = [
            sponsor1FamilyName,
            sponsor1GivenName,
            sponsor1Country,
            sponsor1Relationship,
            sponsor1Occupation,
            sponsor1Currency,
          ].some((val) => val && val !== "");
          return hasAnyValue ? value !== null && value !== undefined : true;
        }
      ),
    sponsor1Currency: Yup.string().test(
      "conditional-required",
      "Please select currency",
      function (value) {
        const {
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
        } = this.parent;
        const hasAnyValue = [
          sponsor1FamilyName,
          sponsor1GivenName,
          sponsor1Country,
          sponsor1Relationship,
          sponsor1Occupation,
          sponsor1Income,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),

    // Sponsor 2
    sponsor2FamilyName: Yup.string().test(
      "conditional-required",
      "Please enter family name",
      function (value) {
        const {
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor2GivenName: Yup.string().test(
      "conditional-required",
      "Please enter given name",
      function (value) {
        const {
          sponsor2FamilyName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor2FamilyName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor2Country: Yup.string().test(
      "conditional-required",
      "Please select country",
      function (value) {
        const {
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor2Relationship: Yup.string().test(
      "conditional-required",
      "Please select relationship",
      function (value) {
        const {
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Occupation,
          sponsor2Income,
          sponsor2Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor2Occupation: Yup.string().test(
      "conditional-required",
      "Please enter occupation",
      function (value) {
        const {
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Income,
          sponsor2Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Income,
          sponsor2Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor2Income: Yup.number()
      .min(0, "Income cannot be negative")
      .test(
        "conditional-required",
        "Please enter annual income",
        function (value) {
          const {
            sponsor2FamilyName,
            sponsor2GivenName,
            sponsor2Country,
            sponsor2Relationship,
            sponsor2Occupation,
            sponsor2Currency,
          } = this.parent;
          const hasAnyValue = [
            sponsor2FamilyName,
            sponsor2GivenName,
            sponsor2Country,
            sponsor2Relationship,
            sponsor2Occupation,
            sponsor2Currency,
          ].some((val) => val && val !== "");
          return hasAnyValue ? value !== null && value !== undefined : true;
        }
      ),
    sponsor2Currency: Yup.string().test(
      "conditional-required",
      "Please select currency",
      function (value) {
        const {
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
        } = this.parent;
        const hasAnyValue = [
          sponsor2FamilyName,
          sponsor2GivenName,
          sponsor2Country,
          sponsor2Relationship,
          sponsor2Occupation,
          sponsor2Income,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),

    // Sponsor 3
    sponsor3FamilyName: Yup.string().test(
      "conditional-required",
      "Please enter family name",
      function (value) {
        const {
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor3GivenName: Yup.string().test(
      "conditional-required",
      "Please enter given name",
      function (value) {
        const {
          sponsor3FamilyName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor3FamilyName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor3Country: Yup.string().test(
      "conditional-required",
      "Please select country",
      function (value) {
        const {
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor3Relationship: Yup.string().test(
      "conditional-required",
      "Please select relationship",
      function (value) {
        const {
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Occupation,
          sponsor3Income,
          sponsor3Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor3Occupation: Yup.string().test(
      "conditional-required",
      "Please enter occupation",
      function (value) {
        const {
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Income,
          sponsor3Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Income,
          sponsor3Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor3Income: Yup.number()
      .min(0, "Income cannot be negative")
      .test(
        "conditional-required",
        "Please enter annual income",
        function (value) {
          const {
            sponsor3FamilyName,
            sponsor3GivenName,
            sponsor3Country,
            sponsor3Relationship,
            sponsor3Occupation,
            sponsor3Currency,
          } = this.parent;
          const hasAnyValue = [
            sponsor3FamilyName,
            sponsor3GivenName,
            sponsor3Country,
            sponsor3Relationship,
            sponsor3Occupation,
            sponsor3Currency,
          ].some((val) => val && val !== "");
          return hasAnyValue ? value !== null && value !== undefined : true;
        }
      ),
    sponsor3Currency: Yup.string().test(
      "conditional-required",
      "Please select currency",
      function (value) {
        const {
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
        } = this.parent;
        const hasAnyValue = [
          sponsor3FamilyName,
          sponsor3GivenName,
          sponsor3Country,
          sponsor3Relationship,
          sponsor3Occupation,
          sponsor3Income,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),

    // Sponsor 4
    sponsor4FamilyName: Yup.string().test(
      "conditional-required",
      "Please enter family name",
      function (value) {
        const {
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor4GivenName: Yup.string().test(
      "conditional-required",
      "Please enter given name",
      function (value) {
        const {
          sponsor4FamilyName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor4FamilyName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor4Country: Yup.string().test(
      "conditional-required",
      "Please select country",
      function (value) {
        const {
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor4Relationship: Yup.string().test(
      "conditional-required",
      "Please select relationship",
      function (value) {
        const {
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Occupation,
          sponsor4Income,
          sponsor4Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor4Occupation: Yup.string().test(
      "conditional-required",
      "Please enter occupation",
      function (value) {
        const {
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Income,
          sponsor4Currency,
        } = this.parent;
        const hasAnyValue = [
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Income,
          sponsor4Currency,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    sponsor4Income: Yup.number()
      .min(0, "Income cannot be negative")
      .test(
        "conditional-required",
        "Please enter annual income",
        function (value) {
          const {
            sponsor4FamilyName,
            sponsor4GivenName,
            sponsor4Country,
            sponsor4Relationship,
            sponsor4Occupation,
            sponsor4Currency,
          } = this.parent;
          const hasAnyValue = [
            sponsor4FamilyName,
            sponsor4GivenName,
            sponsor4Country,
            sponsor4Relationship,
            sponsor4Occupation,
            sponsor4Currency,
          ].some((val) => val && val !== "");
          return hasAnyValue ? value !== null && value !== undefined : true;
        }
      ),
    sponsor4Currency: Yup.string().test(
      "conditional-required",
      "Please select currency",
      function (value) {
        const {
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
        } = this.parent;
        const hasAnyValue = [
          sponsor4FamilyName,
          sponsor4GivenName,
          sponsor4Country,
          sponsor4Relationship,
          sponsor4Occupation,
          sponsor4Income,
        ].some((val) => val && val !== "" && val !== 0);
        return hasAnyValue ? !!value : true;
      }
    ),
    hasFinancialObligationsHome: Yup.boolean()
      .nullable()
      .required("Please indicate if you have financial obligations at home"),
    financialObligationTypes: Yup.array().when("hasFinancialObligationsHome", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string())
          .min(1, "Please select at least one financial obligation type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    hasIncomeGeneratingAssets: Yup.boolean()
      .nullable()
      .required("Please indicate if you have income-generating assets"),
    incomeGeneratingAssetTypes: Yup.array().when("hasIncomeGeneratingAssets", {
      is: true,
      then: (schema) =>
        schema.of(Yup.string()).min(1, "Please select at least one asset type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    financialCapacity: Yup.string()
      .required("Please describe your financial capacity")
      .max(150, "Maximum 150 words allowed"),

    // Research & Understanding substage
    researchConductedActivities: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one research activity"),
    researchedLivingCosts: Yup.boolean()
      .nullable()
      .required("Please indicate if you have researched living costs"),
    estimatedMonthlyExpenses: Yup.number().when("researchedLivingCosts", {
      is: true,
      then: (schema) =>
        schema.required("Please enter your estimated monthly expenses"),
      otherwise: (schema) => schema.notRequired(),
    }),
    estimatedExpensesCurrency: Yup.string().when(
      ["researchedLivingCosts", "estimatedMonthlyExpenses"],
      {
        is: (researched: boolean, expenses: number) =>
          researched === true && expenses > 0,
        then: (schema) => schema.required("Currency is required"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    arrangedAccommodation: Yup.boolean()
      .nullable()
      .required("Please indicate if you have arranged accommodation"),
    accommodationType: Yup.string().when("arrangedAccommodation", {
      is: true,
      then: (schema) =>
        schema.required("Please select your accommodation type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    understandingCourseRequirements: Yup.string().required(
      "Please rate your understanding of course requirements"
    ),
    understandingLivingCosts: Yup.string().required(
      "Please rate your understanding of living costs"
    ),
    understandingVisaConditions: Yup.string().required(
      "Please rate your understanding of visa conditions"
    ),
    understandingWorkRestrictions: Yup.string().required(
      "Please rate your understanding of work restrictions"
    ),
    studyLivingKnowledge: Yup.string()
      .required("Please describe your study and living knowledge")
      .max(150, "Maximum 150 words allowed"),

    // Language & Readiness substage
    englishTestTaken: Yup.string().required(
      "Please select the English test you have taken"
    ),
    englishTestScore: Yup.number()
      .nullable()
      .when("englishTestTaken", {
        is: (val: string) => val && val !== "none",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTestTaken as TestType
                );
              }
            )
            .required("Please enter your test score"),
        otherwise: (schema) => schema.nullable(),
      }),
    englishTestDate: Yup.date()
      .nullable()
      .when("englishTestTaken", {
        is: (val: string) => val && val !== "none",
        then: (schema) => schema.required("Please enter your test date"),
        otherwise: (schema) => schema.notRequired(),
      }),
    hasEducationEmploymentGaps: Yup.boolean()
      .nullable()
      .required("Please indicate if you have education or employment gaps"),
    gapPrimaryActivity: Yup.string().when("hasEducationEmploymentGaps", {
      is: true,
      then: (schema) =>
        schema.required("Please select your primary gap activity"),
      otherwise: (schema) => schema.notRequired(),
    }),
    gapPeriodExplanation: Yup.string().when("hasEducationEmploymentGaps", {
      is: true,
      then: (schema) =>
        schema
          .required("Please explain your gap period")
          .max(150, "Maximum 150 words allowed"),
      otherwise: (schema) => schema.notRequired(),
    }),
    applicationPreparation: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one preparation activity"),
  }),

  // Stage 5: Statement of Purpose
  Yup.object({
    sopGuidelinesAcknowledged: Yup.boolean()
      .nullable()
      .oneOf([true], "You must acknowledge the SOP guidelines"),
    sopRequirementsUnderstood: Yup.boolean()
      .nullable()
      .oneOf([true], "You must confirm understanding of SOP requirements"),
  }),

  // Stage 6: Checklist
  Yup.object({
    checklist: Yup.object({
      isComplete: Yup.boolean().test(
        "checklist-complete",
        "Please complete all required checklist items before proceeding",
        (value) => value === true
      ),
    }).required("Checklist is required"),
  }),

  // Stage 7: Declaration
  Yup.object({
    declarations: Yup.object({
      mandatory: Yup.array()
        .of(
          Yup.object({
            id: Yup.string(),
            text: Yup.string(),
            isAccepted: Yup.boolean(),
          })
        )
        .test("all-accepted", "You must accept all declarations", (value) => {
          return value?.every((d: any) => d.isAccepted) || false;
        })
        .required("You must accept all declarations"),
    }),
  }),

  // Stage 8: Review
  Yup.object({
    // Review stage doesn't require additional validation
  }),
];
